/**
 * PostHog product analytics — direct call style.
 *
 * GDPR-friendly / security-hardened defaults:
 *   - Session recording is OFF.
 *   - IP capture disabled at init.
 *   - capture_pageview FALSE — we trigger pageviews manually on
 *     react-router navigation so SPA route changes are tracked correctly.
 *   - Sensitive properties (password, token) are stripped before send.
 *   - persistence: 'cookie' — distinct_id is kept in a 1st-party cookie
 *     so we never persist user identifiers in localStorage where XSS could
 *     read them. localStorage in this app is reserved strictly for harmless
 *     UI preferences (see /SECURITY.md).
 *
 * Module pattern: we synchronously import posthog-js. The library is
 * happy to queue events internally between init() and the first wire
 * flush — no custom queue needed.
 */
import posthog from 'posthog-js';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

const SENSITIVE_KEYS = ['password', 'token', 'secret', 'api_key', 'auth'];
function sanitize(props) {
  if (!props || typeof props !== 'object') return props;
  const out = { ...props };
  for (const k of Object.keys(out)) {
    if (SENSITIVE_KEYS.some((s) => k.toLowerCase().includes(s))) {
      delete out[k];
    }
  }
  return out;
}

let initialized = false;
function ensureInit() {
  if (initialized || !KEY) return;
  posthog.init(KEY, {
    api_host: HOST,
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
    persistence: 'cookie',
    ip: false,
    respect_dnt: false,
    sanitize_properties: sanitize,
  });
  initialized = true;
  if (typeof window !== 'undefined') {
    window.__hussar_posthog = posthog; // for diagnostics
  }
}

ensureInit();

/**
 * All catch blocks in this module intentionally swallow errors. Analytics
 * is fire-and-forget by design: a PostHog failure must NEVER break a
 * user-visible flow (login, checkout, page nav). In development we surface
 * the underlying error via `console.debug` so engineers can spot
 * misconfiguration without polluting the user-facing console in production.
 */
const _debugAnalyticsError = (label, err) => {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    console.debug(`[analytics:${label}] suppressed:`, err);
  }
};

export function track(event, properties = {}) {
  if (!KEY) return;
  ensureInit();
  try {
    posthog.capture(event, sanitize(properties));
  } catch (err) {
    _debugAnalyticsError('track', err);
  }
}

export function trackPageview(pathname, properties = {}) {
  track('$pageview', {
    $pathname: pathname,
    ...properties,
  });
}

export function identify(distinctId, properties = {}) {
  if (!KEY || !distinctId) return;
  ensureInit();
  try {
    posthog.identify(String(distinctId), sanitize(properties));
  } catch (err) {
    _debugAnalyticsError('identify', err);
  }
}

export function resetIdentity() {
  if (!KEY) return;
  try {
    posthog.reset();
  } catch (err) {
    _debugAnalyticsError('resetIdentity', err);
  }
}

export const isAnalyticsEnabled = Boolean(KEY);
